import moment from "moment";

import UtilityService from "./UtilityService";
import { postToAgents } from "./PlaybookApi";

const KIND_TEXTS = {
	match: "Matches two lists",
	summarize: "Summarizes a list",
	transform: "Transforms a list",
};
const SOURCE_TEXTS = {
	drafted: "Written by Workflow",
	redrafted: "Written again by Workflow",
	edited_by_person: "Changed by a person",
	from_library: "Taken from the firm's scripts",
};
const DIMENSION_LABELS = {
	does_what_was_asked: "Does what was asked",
	data_integrity: "Data integrity",
	accounting: "Accounting",
	failure_handling: "Failure handling",
};
const SECTION_TITLES = {
	unmatchedLeft: "Unmatched on the left",
	unmatchedRight: "Unmatched on the right",
	duplicateKeys: "Duplicate keys",
	rows: "Rows",
};
const EXCEPTION_COUNT_WORDS = {
	unmatchedLeft: "unmatched on the left",
	unmatchedRight: "unmatched on the right",
	duplicateKeys: "duplicate keys",
};
const CHANGE_TEXTS = {
	added: "Added",
	removed: "Removed",
	changed: "Changed",
};
const ACTION_TYPE_WORDS = {
	create_task: "create a task",
	complete_task: "mark a task done",
	add_note: "add a note",
	move_engagement_stage: "move an engagement's stage",
};
const CONFIDENCE_TEXTS = {
	high: "High",
	medium: "Medium",
	low: "Low",
};
const ASKS_WORKFLOW_LEAD = "Asks Workflow to:";
const REQUESTED_ACTIONS_LIST = "requestedActions";
const EXCEPTION_LISTS_IN_ORDER = ["unmatchedLeft", "unmatchedRight", "duplicateKeys"];
const AMOUNT_FIELD_TYPE = "amount";
const PASSED = "passed";
const FAILED_TO_RUN = "failed_to_run";
const FROM_LIBRARY = "library";
const FROM_A_PERSON = "person";
const EMPTY_DATA_PASS_TEXT = "Tested on a month with no rows and no exceptions.";
const EVERY_FINDING_MARKED_TEXT = "Reviewed: every finding is marked.";
const COMPARISON_FAILURE_LEADS = {
	failed_to_run: "The comparison did not run",
	output_refused: "The comparison's output was refused",
};
const DATE_FORMAT = "MMM D, YYYY";
const CLOSED_CONVERSATION_TEXT = "This conversation is closed; its scripts can be read, not changed.";
const NOT_ON_ITS_STEP_TEXT = 'This script is no longer on its step, so it can be read, not changed. To work on it again, choose "Use its script again" on the step.';

const PlaybookScriptService = {
	/**
	 * Every script of a build conversation as the screen shows it, and who is looking: resolves to
	 * { scripts, viewer: { canApprove, areScriptsOn, whyNotApprove } }, the viewer flags given even
	 * when the conversation has no script yet.
	 *
	 * @param {Object} params { sessionId }
	 * @return {Promise<{ scripts: Array<Object>, viewer: { canApprove: Boolean, areScriptsOn: Boolean, whyNotApprove: String|null } }>}
	 */
	listBuildScripts({ sessionId }) {
		return postToAgents({ operationId: "listPlaybookBuildScripts", body: { sessionId } });
	},

	/**
	 * Has Workflow write, or write again, the script for a gap of the conversation; an empty
	 * scriptKey lets Workflow name it.
	 *
	 * @param {Object} params { sessionId, gapId, scriptKey }
	 * @return {Promise<Object>}
	 */
	writeBuildScript({ sessionId, gapId, scriptKey }) {
		return postToAgents({ operationId: "writePlaybookBuildScript", body: { sessionId, gapId, scriptKey } });
	},

	/**
	 * Saves a person's own change to a script of the conversation, a new revision that needs
	 * approval again.
	 *
	 * @param {Object} params { sessionId, pinId, code, manifest, plainDescription }
	 * @return {Promise<Object>}
	 */
	editBuildScript({ sessionId, pinId, code, manifest, plainDescription }) {
		return postToAgents({ operationId: "editPlaybookBuildScript", body: { sessionId, pinId, code, manifest, plainDescription } });
	},

	/**
	 * Tests the current revision of a script on one client and, when the script reads a period, the
	 * month ending on periodEnd (YYYY-MM-DD).
	 *
	 * @param {Object} params { sessionId, pinId, clientId, periodEnd }
	 * @return {Promise<{ testId: Number, outcome: String, reason: String|null, results: Object|null }>}
	 */
	testBuildScript({ sessionId, pinId, clientId, periodEnd }) {
		return postToAgents({ operationId: "testPlaybookBuildScript", body: { sessionId, pinId, clientId, periodEnd } });
	},

	/**
	 * Compares a changed approved script with the test stored when it was approved.
	 *
	 * @param {Object} params { sessionId, pinId }
	 * @return {Promise<{ testId: Number, outcome: String, reason: String|null, differences: Array|null, differencesFingerprint: String|null, shownDifferences: Array|null }>}
	 */
	compareBuildScript({ sessionId, pinId }) {
		return postToAgents({ operationId: "comparePlaybookBuildScript", body: { sessionId, pinId } });
	},

	/**
	 * Has the current revision of a script reviewed, once, as advice.
	 *
	 * @param {Object} params { sessionId, pinId }
	 * @return {Promise<Object>}
	 */
	reviewBuildScript({ sessionId, pinId }) {
		return postToAgents({ operationId: "reviewPlaybookBuildScript", body: { sessionId, pinId } });
	},

	/**
	 * Marks one shown finding of a script's review agreed or disagreed.
	 *
	 * @param {Object} params { sessionId, findingId, verdict } verdict is "agreed" or "disagreed"
	 * @return {Promise<Object>}
	 */
	markBuildScriptFinding({ sessionId, findingId, verdict }) {
		return postToAgents({ operationId: "markPlaybookBuildScriptFinding", body: { sessionId, findingId, verdict } });
	},

	/**
	 * Approves the current revision of a script, sending back exactly the fingerprint of the
	 * comparison's differences the approver saw (null when there was no comparison).
	 *
	 * @param {Object} params { sessionId, pinId, note, confirmedDifferencesFingerprint }
	 * @return {Promise<{ approved: Boolean, nextStep: String }>}
	 */
	approveBuildScript({ sessionId, pinId, note, confirmedDifferencesFingerprint }) {
		return postToAgents({ operationId: "approvePlaybookBuildScript", body: { sessionId, pinId, note, confirmedDifferencesFingerprint } });
	},

	/**
	 * Adds an approved script of the conversation to the firm's scripts, frozen as it was approved.
	 *
	 * @param {Object} params { sessionId, pinId }
	 * @return {Promise<Object>}
	 */
	addScriptToLibrary({ sessionId, pinId }) {
		return postToAgents({ operationId: "addPlaybookScriptToLibrary", body: { sessionId, pinId } });
	},

	/**
	 * Takes a script from the firm's scripts onto a gap of the conversation, under the name given
	 * (scriptKey) or its own.
	 *
	 * @param {Object} params { sessionId, gapId, firmScriptId, scriptKey }
	 * @return {Promise<Object>}
	 */
	takeScriptFromLibrary({ sessionId, gapId, firmScriptId, scriptKey }) {
		return postToAgents({ operationId: "takePlaybookScriptFromLibrary", body: { sessionId, gapId, firmScriptId, scriptKey } });
	},

	/**
	 * The firm's scripts that are not retired, newest first.
	 *
	 * @return {Promise<Array<Object>>}
	 */
	listScriptLibrary() {
		return postToAgents({ operationId: "listPlaybookScriptLibrary", body: {} });
	},

	/**
	 * Retires a script from the firm's scripts so it can no longer be taken.
	 *
	 * @param {Object} params { firmScriptId }
	 * @return {Promise<Object>}
	 */
	retireLibraryScript({ firmScriptId }) {
		return postToAgents({ operationId: "retirePlaybookScriptFromLibrary", body: { firmScriptId } });
	},

	/**
	 * The words and choices of one script, every sentence built from the server's fields and none
	 * written here: what still stops approval is the server's stillNeeded, verbatim, led by the
	 * server's viewer.whyNotApprove when set; who may approve, mark findings or add to the firm's
	 * scripts is the server's viewer.canApprove (and only while scripts are switched on). Every action
	 * flag (canApprove, canCompare, each finding's canMark, canAddToLibrary, canRedraft) is false for a
	 * script not on its step. Whether a comparison is owed is the server's script.canCompare, and its
	 * "Compare this change" line shows only when the card's own canCompare does; the server's
	 * script.whyNotCompare, when set on a script on its step with scripts on, replaces that line
	 * verbatim, and a finished comparison's own words still win over both. The
	 * attempts line is absent when writing again is not offered at all (scripts off, or the script off
	 * its step), the server's whyNotRedraft when the attempts are used up, and otherwise how many are
	 * left. The review line counts only findings with no verdict yet. A passing test with no rows and
	 * no exceptions says so plainly (D6) and isEmptyDataPass is true. reviewRefusal is the server's
	 * sentence when asking for the review was refused (D11), and then stands as the review's words.
	 * The server's script.whyNotReview (who may not ask for the review, D11) stands as the review's
	 * words only while the review could be asked for: a script on its step, scripts on, the
	 * conversation open and no review yet; a refusal from a click still wins over it.
	 * canAskForReview is true only for a workable script with no review and no whyNotReview, so the
	 * review button shows only on the server's word. Differences are hidden only when the server hid
	 * them (no differences, a counted differenceCount); a comparison that did not pass is described
	 * by its outcome and the server's reason. isOnItsGap and readsAPeriod are the server's, passed through. A conversation that is
	 * no longer open can be read, not changed: every action flag is false and, for a script on its
	 * step, closedText says so. A script not on its step gets notOnItsStepText, which names the way
	 * back ("Use its script again"), else null. actionsText lists the Workflow actions the newest
	 * passing test asked for (its stored requestedActions [{ number, type, title }]) as "Asks
	 * Workflow to: …", so an approver who did not run the test sees them; a title the server held
	 * back (null: an off-team viewer, or a passing comparison) shows the action's type in plain
	 * words, and a summary with no list, or an empty one, gives null. Nothing is written.
	 *
	 * @param {Object} params
	 * @param {Object} params.script one entry of listBuildScripts' scripts
	 * @param {Object} params.viewer listBuildScripts' viewer { canApprove, areScriptsOn, whyNotApprove }
	 * @param {String|null} [params.reviewRefusal] the server's sentence when the review was refused
	 * @param {Boolean} [params.isConversationOpen] whether the build conversation is open (default true)
	 * @return {Object} { title, plainDescription, kindText, revisionText, sourceText, attemptsText, canRedraft, testText, actionsText, isEmptyDataPass, reviewText, canAskForReview, findings, compareText, canCompare, canApprove, approveBlockers, approvedText, canAddToLibrary, isOnItsGap, readsAPeriod, closedText, notOnItsStepText }
	 */
	describeScriptCard({ script, viewer, reviewRefusal = null, isConversationOpen = true }) {
		const isOnItsGap = script.isOnItsGap;
		const isWorkable = viewer.areScriptsOn && isOnItsGap && isConversationOpen;
		const isApprover = viewer.canApprove && isWorkable;
		const closedText = isOnItsGap && !isConversationOpen ? CLOSED_CONVERSATION_TEXT : null;
		const notOnItsStepText = isOnItsGap ? null : NOT_ON_ITS_STEP_TEXT;
		const title = `Script "${script.scriptKey}"`;
		const kindText = UtilityService.labelFor({ labels: KIND_TEXTS, key: script.kind });
		const revisionText = `Revision ${script.revisionNumber}`;
		const sourceText = UtilityService.labelFor({ labels: SOURCE_TEXTS, key: script.source });

		const isRedraftOffered = isWorkable;
		const hasAttemptsLeft = script.attemptsLeft > 0;
		const timesWord = script.attemptsLeft === 1 ? "time" : "times";
		let attemptsText = null;
		if (isRedraftOffered && hasAttemptsLeft) attemptsText = `Workflow can write it ${script.attemptsLeft} more ${timesWord}.`;
		if (isRedraftOffered && !hasAttemptsLeft) attemptsText = script.whyNotRedraft;
		const canRedraft = isRedraftOffered && hasAttemptsLeft && !script.whyNotRedraft;

		const newestTest = script.newestTest;
		const isPassed = Boolean(newestTest) && newestTest.outcome === PASSED;
		const exceptionCounts = isPassed && newestTest.exceptionCounts ? newestTest.exceptionCounts : {};
		const exceptionLists = Object.keys(exceptionCounts);
		const exceptionTotal = exceptionLists.reduce((total, list) => total + exceptionCounts[list], 0);
		const isEmptyDataPass = isPassed && newestTest.rowCount === 0 && exceptionTotal === 0;
		const rowWord = isPassed && newestTest.rowCount === 1 ? "row" : "rows";
		const countParts = exceptionLists.map(list => `${exceptionCounts[list]} ${UtilityService.labelFor({ labels: EXCEPTION_COUNT_WORDS, key: list })}`);
		const countsText = isPassed ? [`${newestTest.rowCount} ${rowWord}`, ...countParts].join(", ") : "";
		const hasPeriod = Boolean(newestTest) && Boolean(newestTest.period);
		const periodText = hasPeriod ? ` for the month ending ${moment(newestTest.period.periodEnd).format(DATE_FORMAT)}` : "";
		const reasonText = newestTest && newestTest.reason ? `: ${newestTest.reason}` : ".";
		const failureLead = newestTest && newestTest.outcome === FAILED_TO_RUN ? "Did not run" : "Its output was refused";
		let testText = "Not tested yet.";
		if (newestTest && !isPassed) testText = `${failureLead}${periodText}${reasonText}`;
		if (isPassed) testText = `Passed${periodText}: ${countsText}.`;
		if (isEmptyDataPass) testText = EMPTY_DATA_PASS_TEXT;
		const storedActions = isPassed && newestTest.requestedActions ? newestTest.requestedActions : [];
		const actionNames = storedActions.map(action => {
			const hasTitle = action.title !== null && action.title !== undefined;
			const actionName = hasTitle ? action.title : UtilityService.labelFor({ labels: ACTION_TYPE_WORDS, key: action.type });
			return actionName;
		});
		const actionsText = actionNames.length > 0 ? `${ASKS_WORKFLOW_LEAD} ${actionNames.join("; ")}.` : null;

		const review = script.review;
		const shownFindings = review ? review.shownFindings : [];
		const unmarkedFindings = shownFindings.filter(finding => !finding.verdict);
		const findingWord = unmarkedFindings.length === 1 ? "finding" : "findings";
		const isEveryFindingMarked = shownFindings.length > 0 && unmarkedFindings.length === 0;
		const isOneHidden = Boolean(review) && review.hiddenCount === 1;
		const hiddenWords = isOneHidden ? "finding gave no evidence, so it is" : "findings gave no evidence, so they are";
		const hiddenText = review && review.hiddenCount > 0 ? ` ${review.hiddenCount} more ${hiddenWords} not shown.` : "";
		const whyNotReview = isWorkable && !review ? script.whyNotReview : null;
		const canAskForReview = isWorkable && !review && !script.whyNotReview;
		let reviewText = "Not reviewed yet.";
		if (whyNotReview) reviewText = whyNotReview;
		if (review && shownFindings.length === 0) reviewText = `Reviewed: no findings to mark.${hiddenText}`;
		if (review && unmarkedFindings.length > 0) reviewText = `Reviewed: ${unmarkedFindings.length} ${findingWord} to mark.${hiddenText}`;
		if (review && isEveryFindingMarked) reviewText = `${EVERY_FINDING_MARKED_TEXT}${hiddenText}`;
		if (reviewRefusal) reviewText = reviewRefusal;
		const findings = shownFindings.map(finding => {
			const dimensionLabel = UtilityService.labelFor({ labels: DIMENSION_LABELS, key: finding.dimension });
			const label = finding.location ? `${dimensionLabel} · ${finding.location}` : dimensionLabel;
			const described = { findingId: finding.findingId, label, detail: finding.issue, evidence: finding.evidence, verdict: finding.verdict, canMark: isApprover };
			return described;
		});

		const isCompareOwed = script.canCompare === true;
		const isCompareActionable = isWorkable;
		const canCompare = isCompareActionable && isCompareOwed;
		const differences = PlaybookScriptService.describeScriptDifferences({ regression: script.regression, manifest: script.manifest });
		const differenceCount = differences.differenceCount;
		const differenceWord = differenceCount === 1 ? "difference" : "differences";
		const hasRegression = Boolean(script.regression);
		const whyNotCompare = isCompareActionable ? script.whyNotCompare : null;
		let compareText = null;
		if (canCompare) compareText = "Compare this change with the test stored when it was approved.";
		if (whyNotCompare) compareText = whyNotCompare;
		if (hasRegression && differenceCount === 0) compareText = "Compared with its stored test: no differences.";
		if (hasRegression && differenceCount > 0) compareText = `Compared with its stored test: ${differenceCount} ${differenceWord}.`;
		if (differences.isHidden && differenceCount > 0) compareText = `Compared with its stored test: ${differenceCount} ${differenceWord}, shown only to the client's team.`;
		if (differences.failureText) compareText = differences.failureText;

		const canApprove = isApprover && script.canApprove && !script.isApproved;
		const approverBlockers = viewer.whyNotApprove ? [viewer.whyNotApprove] : [];
		const approveBlockers = script.isApproved ? script.stillNeeded : [...approverBlockers, ...script.stillNeeded];

		const approvedOn = script.approvedAt ? moment(script.approvedAt).format(DATE_FORMAT) : "";
		const approverName = script.approvedBy || "someone no longer listed";
		const isFromLibrary = script.approvalSource === FROM_LIBRARY;
		const approvedLead = isFromLibrary ? "Approved from the firm's scripts, by" : "Approved by";
		const approvedText = script.isApproved ? `${approvedLead} ${approverName} on ${approvedOn}.` : null;
		const canAddToLibrary = isApprover && script.isApproved && script.approvalSource === FROM_A_PERSON;

		const card = {
			title,
			plainDescription: script.plainDescription,
			kindText,
			revisionText,
			sourceText,
			attemptsText,
			canRedraft,
			testText,
			actionsText,
			isEmptyDataPass,
			reviewText,
			canAskForReview,
			findings,
			compareText,
			canCompare,
			canApprove,
			approveBlockers,
			approvedText,
			canAddToLibrary,
			isOnItsGap,
			readsAPeriod: script.readsAPeriod,
			closedText,
			notOnItsStepText,
		};
		return card;
	},

	/**
	 * A test's results as tables a person reads: the columns in the order the manifest's output
	 * declares its fields, each amount field marked; the exception lists first (unmatched on the
	 * left, unmatched on the right, duplicate keys, then any other list the results carry), then the
	 * rows. Amounts arrive from the server as dollars text; a cell only adds thousands separators
	 * through UtilityService.formatCurrency, for display. An amount that is not dollars text (an
	 * empty one, or a number) is shown as it came, so nothing is ever converted here. An empty value
	 * is an empty cell. actions are the Workflow actions the test's output asks for, from the
	 * server's requestedActions [{ number, type, reason, confidence, preview }]: each with the
	 * checked preview's title, its confidence in words and the checked reason (which names the
	 * script and revision); an empty list when the results carry none.
	 *
	 * @param {Object} params
	 * @param {Object} params.results testBuildScript's results { exceptions, rows, requestedActions }
	 * @param {Object} params.manifest the script's manifest, whose output.fields gives each field's type
	 * @return {{ sections: Array<{ title: String, isException: Boolean, rows: Array<{ key: String, cells: Array<String> }> }>, columns: Array<{ field: String, isAmount: Boolean }>, actions: Array<{ key: String, title: String, confidence: String, reason: String }> }}
	 */
	describeScriptResults({ results, manifest }) {
		const fields = manifest && manifest.output ? manifest.output.fields : {};
		const fieldNames = Object.keys(fields);
		const columns = fieldNames.map(field => ({ field, isAmount: fields[field] === AMOUNT_FIELD_TYPE }));
		const exceptions = results.exceptions || {};
		const givenLists = Object.keys(exceptions);
		const knownLists = EXCEPTION_LISTS_IN_ORDER.filter(list => givenLists.includes(list));
		const otherLists = givenLists.filter(list => !EXCEPTION_LISTS_IN_ORDER.includes(list));
		const orderedLists = [...knownLists, ...otherLists];

		const cellsOf = values =>
			columns.map(column => {
				const value = values ? values[column.field] : null;
				const isEmpty = value === null || value === undefined;
				const isDollarsText = column.isAmount && typeof value === "string";
				if (isEmpty) return "";
				if (isDollarsText) return UtilityService.formatCurrency(value);
				return String(value);
			});
		const exceptionSections = orderedLists.map(list => ({
			title: UtilityService.labelFor({ labels: SECTION_TITLES, key: list }),
			isException: true,
			rows: exceptions[list].map((item, index) => ({ key: `${list}-${index}`, cells: cellsOf(item.values) })),
		}));
		const givenRows = results.rows || [];
		const rowSection = {
			title: SECTION_TITLES.rows,
			isException: false,
			rows: givenRows.map(row => ({ key: `rows-${row.rowId}`, cells: cellsOf(row.values) })),
		};
		const sections = [...exceptionSections, rowSection];
		const requestedActions = results.requestedActions || [];
		const actions = requestedActions.map(action => ({
			key: `action-${action.number}`,
			title: action.preview.title,
			confidence: UtilityService.labelFor({ labels: CONFIDENCE_TEXTS, key: action.confidence }),
			reason: action.reason,
		}));
		const described = { sections, columns, actions };
		return described;
	},

	/**
	 * A comparison's differences as a person reads them, from the server's shownDifferences (each
	 * side's amounts already dollars text): each one "Added", "Removed" or "Changed", with the list it
	 * is in and its before and after cells; the exception lists' differences first, then the rows'.
	 * The columns are the current manifest's fields in order, then any other field either side
	 * carries (an edit can rename an output field, so the before side may hold the approved
	 * revision's names), never dropped; an amount column is one the current manifest declares an
	 * amount, its dollars text given thousands separators by UtilityService.formatCurrency, for
	 * display only. A side that is missing is null. The differences are hidden only when the server
	 * hid them (they hold a client's figures and the viewer is off its team: no differences list and a
	 * counted differenceCount); then isHidden is true, rows are empty and differenceCount still counts
	 * them. A comparison whose outcome is not passed (failed_to_run, output_refused) is never hidden:
	 * failureText describes it by its outcome and the server's reason, verbatim, and it has no rows.
	 * A difference in what the script asks Workflow to do (list "requestedActions", each side a
	 * whole script action { type, reason, confidence, ...its fields }, "added" or "removed" only) is
	 * kept out of the rows and the columns, so an action's fields never become columns of the
	 * output's rows: each is one actionLine, "Asks Workflow to: <the type in plain words>" with its
	 * change, the script's reason and details: the action's own fields in plain words, each only
	 * when given, joined by " · " (the task name, its description, "due in N days" / "due today",
	 * "assigned to user <ids>", "task <id>", "engagement <id>", "to stage <id>", the note's text,
	 * "<High|Medium|Low> confidence"). The server compares whole actions, so an edit that changes
	 * only a deadline or an assignee is one "removed" and one "added" whose details differ, and the
	 * approver sees what changed. A script action carries no title or names (those need the live
	 * check a comparison does not run), so the type and raw ids name it. differenceCount still
	 * counts them.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.regression a script's regression, or compareBuildScript's answer
	 * @param {Object} params.manifest the script's current manifest
	 * @return {{ columns: Array<{ field: String, isAmount: Boolean }>, rows: Array<{ key: String, listTitle: String, changeText: String, before: Array<String>|null, after: Array<String>|null }>, actionLines: Array<{ key: String, changeText: String, text: String, details: String, reason: String }>, isHidden: Boolean, differenceCount: Number, failureText: String|null }}
	 */
	describeScriptDifferences({ regression, manifest }) {
		const fields = manifest && manifest.output ? manifest.output.fields : {};
		const fieldNames = Object.keys(fields);
		const hasRegression = Boolean(regression);
		const hasOutcome = hasRegression && Boolean(regression.outcome);
		const isFailedComparison = hasOutcome && regression.outcome !== PASSED;
		const hasNoDifferencesList = hasRegression && (regression.differences === null || regression.differences === undefined);
		const isHidden = hasNoDifferencesList && typeof regression.differenceCount === "number";
		const shownDifferences = hasRegression && regression.shownDifferences ? regression.shownDifferences : [];
		const differenceCount = isHidden ? regression.differenceCount : shownDifferences.length;
		const failureLead = isFailedComparison ? UtilityService.labelFor({ labels: COMPARISON_FAILURE_LEADS, key: regression.outcome }) : "";
		const failureReasonText = isFailedComparison && regression.reason ? `: ${regression.reason}` : ".";
		const failureText = isFailedComparison ? `${failureLead}${failureReasonText}` : null;

		const actionDifferences = shownDifferences.filter(difference => difference.list === REQUESTED_ACTIONS_LIST);
		const tableDifferences = shownDifferences.filter(difference => difference.list !== REQUESTED_ACTIONS_LIST);
		const sidesValues = tableDifferences.flatMap(difference => [difference.before, difference.after]);
		const presentSidesValues = sidesValues.filter(Boolean);
		const sideFieldNames = presentSidesValues.flatMap(values => Object.keys(values));
		const extraFieldNames = sideFieldNames.filter(field => !fieldNames.includes(field));
		const uniqueExtraFieldNames = [...new Set(extraFieldNames)];
		const columnFieldNames = [...fieldNames, ...uniqueExtraFieldNames];
		const columns = columnFieldNames.map(field => ({ field, isAmount: fields[field] === AMOUNT_FIELD_TYPE }));

		const cellsOf = values => {
			if (!values) return null;
			const cells = columns.map(column => {
				const value = values[column.field];
				const isEmpty = value === null || value === undefined;
				const isDollarsText = column.isAmount && typeof value === "string";
				if (isEmpty) return "";
				if (isDollarsText) return UtilityService.formatCurrency(value);
				return String(value);
			});
			return cells;
		};
		const exceptionDifferences = tableDifferences.filter(difference => difference.list !== "rows");
		const rowDifferences = tableDifferences.filter(difference => difference.list === "rows");
		const orderedDifferences = [...exceptionDifferences, ...rowDifferences];
		const rows = orderedDifferences.map((difference, index) => ({
			key: `${difference.list}-${difference.rowId}-${index}`,
			listTitle: UtilityService.labelFor({ labels: SECTION_TITLES, key: difference.list }),
			changeText: UtilityService.labelFor({ labels: CHANGE_TEXTS, key: difference.change }),
			before: cellsOf(difference.before),
			after: cellsOf(difference.after),
		}));
		const actionLines = actionDifferences.map((difference, index) => {
			const action = difference.change === "removed" ? difference.before : difference.after;
			const typeWords = UtilityService.labelFor({ labels: ACTION_TYPE_WORDS, key: action.type });
			const isGiven = value => value !== null && value !== undefined;
			const hasAssignees = Array.isArray(action.assigneeUserIds) && action.assigneeUserIds.length > 0;
			const dayWord = action.deadlineInDays === 1 ? "day" : "days";
			const dueText = action.deadlineInDays === 0 ? "due today" : `due in ${action.deadlineInDays} ${dayWord}`;
			const confidenceWords = UtilityService.labelFor({ labels: CONFIDENCE_TEXTS, key: action.confidence });
			const detailParts = [
				isGiven(action.name) ? `"${action.name}"` : null,
				isGiven(action.description) ? action.description : null,
				isGiven(action.deadlineInDays) ? dueText : null,
				hasAssignees ? `assigned to user ${action.assigneeUserIds.join(", ")}` : null,
				isGiven(action.taskId) ? `task ${action.taskId}` : null,
				isGiven(action.engagementId) ? `engagement ${action.engagementId}` : null,
				isGiven(action.toStageId) ? `to stage ${action.toStageId}` : null,
				isGiven(action.content) ? `"${action.content}"` : null,
				isGiven(action.confidence) ? `${confidenceWords} confidence` : null,
			];
			const givenDetailParts = detailParts.filter(Boolean);
			const actionLine = {
				key: `${REQUESTED_ACTIONS_LIST}-${index}`,
				changeText: UtilityService.labelFor({ labels: CHANGE_TEXTS, key: difference.change }),
				text: `${ASKS_WORKFLOW_LEAD} ${typeWords}`,
				details: givenDetailParts.join(" · "),
				reason: action.reason,
			};
			return actionLine;
		});
		const described = { columns, rows, actionLines, isHidden, differenceCount, failureText };
		return described;
	},
};

export default PlaybookScriptService;
